import type { MantineStyleSystemProps, MantineTheme } from '@mantine/core';
import {
	Avatar,
	Box,
	Flex,
	Group,
	Stack,
	createStyles,
	useMantineTheme,
} from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import {
	Badge,
	Button,
	type ButtonVariants,
	Icon,
	type IconNames,
	Text,
} from '@repo/foundations';
import type { TablerIconsProps } from '@tabler/icons-react';
import { isEmpty, isNil, size, slice } from 'lodash-es';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef, memo, useCallback, useMemo } from 'react';
import { useNavigate } from '../../hooks/useNavigate';
import {
	getUserInitialsFromName,
	pickColorType,
	truncateWithEllipsis,
} from '../../utils';
import GroupIcon from '../GroupIcon/GroupIcon';
import { IconEmoji } from '../IconEmoji/IconEmoji';
import type { ItemIconType } from '../ItemIcon';
import RichTooltip from '../RichTooltip/RichTooltip';
import type { SelectablePropertyType } from '../SelectableProperty/types';
import type { SelectablePropertyItem } from '../SingleSelector/types';
import TagIcon from '../TagIcon/TagIcon';
import { colorGroupToFillMap } from '../UserAvatar/helpers';
import MultiTargetButton from './MultiTargetButton';

const ICON_HEIGHT = 16;
const TRUNCATE_LENGTH = 20;

export interface IMultiSelectorTargetProps extends MantineStyleSystemProps {
	placeholder?: string | null;
	placeholderIconName?: IconNames;
	variant?: ButtonVariants;
	inheritSelected?: SelectablePropertyItem[];
	selected: SelectablePropertyItem[];
	property?: SelectablePropertyType;
	propertyLabel?: string;
	iconType?: ItemIconType;
	isMenuItemBadge?: boolean;
	hideOnEmpty?: boolean;
	isViewerUser?: boolean;
	permittedId?: string;
	readOnly?: boolean;
	withDescriptionTooltip?: boolean;
	onTargetClick?: () => void;
	isChromeUser?: boolean;
	truncate?: boolean;
	className?: string;
}

interface IStyleParams {
	variant?: ButtonVariants;
	readOnly?: boolean;
	hideOnEmpty?: boolean;
	empty?: boolean;
}

const useStyles = createStyles(
	(
		theme: MantineTheme,
		{ variant, readOnly, hideOnEmpty, empty }: IStyleParams
	) => ({
		avatarLabel: {
			fontSize: '0.6rem',
			lineHeight: theme.fontSizes.xs,
		},
		buttonLabel: {
			'.mantine-Avatar-root': {
				outlineColor: theme.other.getColor('surface/app/default'),
			},
			'&:hover': {
				'.mantine-Avatar-root': {
					outlineColor: readOnly
						? 'none'
						: theme.other.getColor('surface/primary/active'),
				},
			},
		},
		badgeInner: {
			color: theme.other.getColor('text/primary/default'),
			fontSize: theme.fontSizes.sm,
			fontWeight: theme.other.typography.weight.regular,
			lineHeight: `${ICON_HEIGHT}px`,
		},
		emoji: {
			fontSize: ICON_HEIGHT,
			lineHeight: `${ICON_HEIGHT}px`,
		},
		content: {
			cursor: 'pointer',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
		},
		placeholder: {
			textOverflow: 'ellipsis',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			color:
				variant === 'primary'
					? theme.other.getColor('text/brand-on-fill/default')
					: theme.other.getColor('text/secondary/default'),
		},
		noShrink: {
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			padding: theme.spacing['4xs'],
			flexWrap: 'nowrap',
		},
		button: {
			padding: 0,
			visibility: hideOnEmpty && empty ? 'hidden' : 'visible',
			'&:hover': {
				visibility: 'visible',
			},
		},
		buttonInner: {
			paddingLeft: theme.spacing['2xs'],
			paddingRight: theme.spacing['xs'],
		},
	})
);

const LeftSection = memo(
	({
		item,
		iconType,
		classes,
	}: {
		item: SelectablePropertyItem;
		iconType: ItemIconType;
		classes: Record<string, string>;
	}) => {
		const theme = useMantineTheme();

		if (iconType === 'avatar') {
			const colorGroup = item.color || pickColorType(item.label);
			const { fillStart, fillEnd, textColor } = colorGroupToFillMap(colorGroup);

			if (item.navigateTo && item.navigateTo.includes('/group/')) {
				return <GroupIcon item={item} />;
			}

			return (
				<Avatar
					radius={`${ICON_HEIGHT}px`}
					miw={`${ICON_HEIGHT}px`}
					mih={`${ICON_HEIGHT}px`}
					h={`${ICON_HEIGHT}px`}
					w={`${ICON_HEIGHT}px`}
					size="sm"
					src={item.icon as string}
					classNames={{
						placeholder: classes.avatarLabel,
					}}
					sx={{
						color: textColor,
						backgroundImage: theme.fn.gradient({
							from: fillStart,
							to: fillEnd,
							deg: 180,
						}),
					}}
				>
					{(item.icon as ReactNode) ||
						slice(getUserInitialsFromName(item.label), 0, 1)}
				</Avatar>
			);
		}
		if (iconType === 'tag') {
			return (
				<TagIcon
					color={item.color || theme.other.getColor('icon/brand/default')}
				/>
			);
		}

		if (iconType === 'tabler') {
			const TablerIconComponent = item.icon as (
				props: TablerIconsProps
			) => JSX.Element;

			return <TablerIconComponent size="md" color={item.color} />;
		}

		if (typeof item.icon === 'string') {
			return (
				<IconEmoji value={item.icon as string} padding={0} size={ICON_HEIGHT} />
			);
		}

		return <Text className={classes.emoji}>{item.icon as ReactNode}</Text>;
	}
);

LeftSection.displayName = 'LeftSection';

const SingleMultiSelectorTarget = memo(
	({
		item,
		variant,
		isMenuItemBadge,
		iconType,
		truncate = true,
		showLabelDetails = false,
		onTargetClick,
		placeholderIconName,
	}: {
		item: SelectablePropertyItem;
		variant: ButtonVariants;
		isMenuItemBadge: boolean;
		iconType: ItemIconType;
		truncate?: boolean;
		showLabelDetails?: boolean;
		onTargetClick?: () => void;
		placeholderIconName?: IconNames;
	}) => {
		const { classes, theme } = useStyles({
			variant,
			readOnly: true,
		});

		const leftSection = useMemo(() => {
			if (iconType !== 'none') {
				return (
					<LeftSection item={item} iconType={iconType} classes={classes} />
				);
			}
			if (placeholderIconName) {
				return <Icon name={placeholderIconName} size="md" />;
			}

			return null;
		}, [classes, iconType, item, placeholderIconName]);

		const label = truncate
			? truncateWithEllipsis(item.label, TRUNCATE_LENGTH)
			: item.label;

		if (isMenuItemBadge && variant === 'tertiary') {
			return (
				<Flex onClick={onTargetClick}>
					<Badge
						classNames={{
							inner: classes.badgeInner,
						}}
						style={{
							backgroundColor: theme.other.getColor(
								'fill/transparent-secondary/default'
							),
						}}
						leftSection={leftSection}
						my={0}
					>
						{label}
					</Badge>
				</Flex>
			);
		}

		return (
			<Group spacing="3xs" noWrap align="center" onClick={onTargetClick}>
				{!isNil(leftSection) && (
					<Box className={classes.noShrink}>{leftSection}</Box>
				)}
				{placeholderIconName && (
					<Icon
						name={placeholderIconName}
						size="md"
						color="icon/secondary/default"
					/>
				)}
				<Stack spacing={0}>
					<Text
						className={classes.content}
						td={item?.strikeThrough ? 'line-through' : undefined}
						size="sm"
						color={
							variant === 'primary'
								? 'text/brand-on-fill/default'
								: 'text/primary/default'
						}
					>
						{label}
					</Text>
					{showLabelDetails && item.labelDetails && (
						<Text size="xs" color="text/secondary/default" lineClamp={1}>
							{item.labelDetails}
						</Text>
					)}
				</Stack>
			</Group>
		);
	}
);
SingleMultiSelectorTarget.displayName = 'SingleMultiSelectorTarget';

const MultiSelectorTarget = forwardRef(
	(
		{
			placeholder = 'Empty',
			propertyLabel,
			placeholderIconName,
			variant = 'tertiary',
			selected: withoutInheritSelected,
			inheritSelected = [],
			property = 'tags',
			iconType = 'tag',
			isMenuItemBadge = false,
			isViewerUser = false,
			permittedId = '',
			readOnly = false,
			hideOnEmpty = false,
			w,
			withDescriptionTooltip = false,
			onTargetClick,
			isChromeUser = false,
			truncate = false,
			...others
		}: IMultiSelectorTargetProps,
		forwardedRef: ForwardedRef<HTMLButtonElement | HTMLDivElement>
	) => {
		const empty =
			isEmpty(withoutInheritSelected) || isNil(withoutInheritSelected);

		const { classes } = useStyles({
			variant,
			readOnly,
			hideOnEmpty,
			empty,
		});

		const ref = useMergedRef(forwardedRef);

		const navigate = useNavigate();

		const selected = useMemo(
			() => [
				...inheritSelected,
				...withoutInheritSelected.filter(
					(item) =>
						!inheritSelected.find(
							(inheritedItem) => inheritedItem.value === item.value
						)
				),
			],
			[inheritSelected, withoutInheritSelected]
		);

		const handleOnClick = useCallback(() => {
			if (isViewerUser) {
				if (
					size(selected) === 1 &&
					!isNil(selected[0].navigateTo) &&
					size(permittedId) === 0
				) {
					navigate(selected[0].navigateTo);
				}
			}
		}, [isViewerUser, navigate, permittedId, selected]);

		const leftSection = useMemo(() => {
			if (isEmpty(selected)) {
				return null;
			}

			if (size(selected) === 1) {
				return (
					<LeftSection
						item={selected[0]}
						iconType={iconType}
						classes={classes}
					/>
				);
			}

			return null;
		}, [classes, iconType, selected]);

		const showTooltip = useMemo(
			() =>
				!isChromeUser &&
				(selected.length > 1 ||
					(selected.length === 1 &&
						selected[0].label.length > TRUNCATE_LENGTH)),
			[selected, isChromeUser]
		);

		const tooltipBody = useMemo(
			() => (
				<Stack spacing={'xs'}>
					{selected.map((el) => (
						<SingleMultiSelectorTarget
							iconType={iconType}
							key={`${el.label}${el.value}`}
							item={el}
							variant={variant}
							isMenuItemBadge={isMenuItemBadge}
							truncate={truncate}
							showLabelDetails
							onTargetClick={() => {
								if (el.navigateTo) {
									navigate(el.navigateTo);
								}
								onTargetClick?.();
							}}
						/>
					))}
				</Stack>
			),
			[
				selected,
				iconType,
				variant,
				isMenuItemBadge,
				onTargetClick,
				navigate,
				truncate,
			]
		);

		const buttonClassNames = useMemo(
			() => ({
				container: classes.button,
				inner: classes.buttonInner,
				label: classes.buttonLabel,
			}),
			[classes]
		);

		return (
			<RichTooltip hidden={!showTooltip} body={tooltipBody}>
				<Button
					w={w}
					size="md"
					onClick={handleOnClick}
					variant={variant}
					ref={ref as ForwardedRef<HTMLButtonElement>}
					classNames={buttonClassNames}
					{...others}
				>
					{size(selected) === 0 && (
						<Group
							aria-label="placeholder"
							spacing="3xs"
							noWrap
							onClick={onTargetClick}
							className={classes.button}
						>
							{!isNil(leftSection) && (
								<Box className={classes.noShrink}>{leftSection}</Box>
							)}
							{placeholderIconName && (
								<Icon
									name={placeholderIconName}
									size="md"
									color="icon/secondary/default"
								/>
							)}
							{placeholder && (
								<Text className={classes.placeholder} size="sm">
									{placeholder}
								</Text>
							)}
						</Group>
					)}
					{size(selected) === 1 && (
						<RichTooltip
							title={selected[0].label}
							body={selected[0].description}
							position="bottom"
							hidden={!withDescriptionTooltip || !selected[0].description}
						>
							<SingleMultiSelectorTarget
								iconType={iconType}
								item={selected[0]}
								variant={variant}
								isMenuItemBadge={isMenuItemBadge}
								onTargetClick={onTargetClick}
								truncate
							/>
						</RichTooltip>
					)}
					{size(selected) > 1 && (
						<MultiTargetButton
							variant={variant}
							selected={selected}
							property={property}
							propertyLabel={propertyLabel}
							iconType={iconType}
							onTargetClick={onTargetClick}
						/>
					)}
				</Button>
			</RichTooltip>
		);
	}
);

MultiSelectorTarget.displayName = 'MultiSelectorTarget';

export default MultiSelectorTarget;
